import { css, keyframes } from "@emotion/react";

import {
  Button,
  Stack,
  Box,
  Slide,
  Slider,
  styled,
  ClickAwayListener,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { tease } from "../components/CheckButton";
import {
  ButtonStyle,
  Color,
  ContactType,
  ErrorCode,
  FontStyle,
  navPaddingTop,
  QuestionData,
  roleTitles,
  SurveyColumn,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HelpIcon from "@mui/icons-material/Help";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  StyledInput,
  StyledTextField,
  StyledTextFieldSmall,
} from "../components/AutocompleteUtils";
import { useDispatch, useSelector } from "react-redux";
import { stringEquals } from "../utils/utils";
import SettingsSort from "../components/SettingsSort";
import EmailSlider from "../components/EmailSlider";
import * as adminService from "../service/AdminApiService";
import { useNavigate } from "react-router-dom";
import bgImage from "../img/staircaseFilter.webp";
import { logout } from "../service/AuthService";
import { clearError } from "../utils/actionUtils";
import TempDisplay from "../components/TempDisplay";
export const bgColorFade = () => keyframes`
    0% {
        background-color: rgba(0, 0, 0, 0);
    }
    100% {
        background-color: rgba(0, 0, 0, 0.5);
    }
`;
export const bgFallDown = () => keyframes`
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(0%);
    }
`;
export const bgImageSquishIn = () => keyframes`
    0% {
      transform: translateY(100%);
    }
    80% {
      transform: translateY(-4%);
    }
    100% {
      transform: translateY(0%);
    }
`;
export const slideInRight = () => keyframes`
    0% {
        transform: translateX(100vw);
    }
    100% {
        transform: translateX(0%);
    }
`;
const styles = {
  outer: {
    paddingTop: navPaddingTop,
    position: "relative",
  },
  container: {
    // marginTop: '67px',
    paddingTop: "2rem",
    paddingBottom: "110px",
    px: 4,
    // backgroundColor: "#fff",
    height: "100%",
    width: "100%",
    display: "flex",
    minHeight: "calc(100vh - 67px)",
    ...FontStyle.body,
  },
  bg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    // backgroundColor: "rgba(0, 0, 0, 1)",
    backgroundImage:
      "radial-gradient( circle 600px at 9% 8%,  #FFFC00 0%, #BBB0D0 39%, #7EE7F2 110% )",
    zIndex: 1,
    animation: `${bgFallDown()} 0.5s ease-in-out 0s`,
  },
  bgImage: {
    position: "fixed",
    bottom: 0,
    right: "4vmin",
    width: "80vmin",
    height: `${(3521 / 3994) * 80}vmin`,
    zIndex: 2,
    transform: "translateY(100%)",
    animation: `${bgImageSquishIn()} 0.4s cubic-bezier(.66,.5,.75,.68)`,
    animationDelay: "0.5s",
    animationFillMode: "forwards",
  },
  inner: {
    // backgroundColor: "white",
    zIndex: 3,
    maxWidth: "1300px",
    // width: "100%",
  },
  panel: {
    backgroundColor: Color.primary + "d0",
    color: "#000",
    borderRadius: "0.5rem",
    boxShadow: "0 0 1rem rgba(255, 255, 0, 0.5)",
    "& .MuiFormControl-root": {
      border: "none",
    },
  },
  heading: {
    ...FontStyle.headingBody,
    padding: "0.5rem 3rem",
    fontSize: "48px",
    textAlign: "center",
    transform: "translateX(100vw)",
    animation: `${slideInRight()} 0.3s ease-in-out 0s`,
    animationDelay: "0.9s",
    animationFillMode: "forwards",
  },
  bodyContainer: {
    padding: "0.5rem 1rem",
    width: "100%",
    height: "100%",
    minHeight: "calc(100vh - 240px)",
    transform: "translateX(100vw)",
    animation: `${slideInRight()} 0.3s ease-in-out 0s`,
    animationDelay: "1.1s",
    animationFillMode: "forwards",
    minWidth: "700px",
    maxWidth: "700px",
    // minHeight: "100%",
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "14px",
  },
  input: {
    width: "100%",
    maxWidth: "500px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  error: {
    ...FontStyle.headingBody,
    fontSize: "14px",
    color: "red",
  },
};
const LabelInputStack = ({ label, children }) => {
  return (
    <Stack
      direction="column"
      spacing="0.5rem"
      sx={{
        width: "100%",
      }}
      alignItems="flex-start">
      <Box sx={{ ...styles.label }}>{label}</Box>
      {children}
    </Stack>
  );
};
const SimpleSelect = ({ label, list, value, ...props }) => {
  return (
    <LabelInputStack label={label}>
      <Select
        sx={{
          ...styles.search,
          width: "100%",
          color: value === list[0] ? "rgba(162,162,162)" : "#000",
        }}
        input={<StyledInput />}
        value={value}
        {...props}>
        {list.map((v, index) => (
          <MenuItem key={"m" + index} value={v} sx={{ ...FontStyle.body }}>
            {v}
          </MenuItem>
        ))}
      </Select>
    </LabelInputStack>
  );
};
const emailTypes = ["default", "email", "google"];
const hiddenQuestionIdx = [2, 10, 11, 12, 14, 15];

const extraLabelHelp = {
  // "Line of work":
  //   " Selected categories are listed first, then selected roles are listed after.",
};

const QuestionLabel = ({ q }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      sx={{
        ...FontStyle.headingBody,
        ...(hovered && {
          cursor: "pointer",
        }),
        // color: "#888",
      }}
      onMouseLeave={() => setHovered(false)}>
      {q.short}{" "}
      {hovered && (
        <span style={{ color: "#888", fontWeight: 400 }}>
          ({q.question}){extraLabelHelp[q.short] && extraLabelHelp[q.short]}
        </span>
      )}
      {!hovered && (
        <HelpIcon
          onMouseEnter={() => setHovered(true)}
          sx={{ fontSize: "12px", color: "#888", cursor: "pointer" }}
        />
      )}
    </Box>
  );
};
const UserSelectedValue = ({ questionIdx, v }) => {
  if (questionIdx === 4 && roleTitles.includes(v)) {
    return (
      <Box sx={{ color: "#111", fontStyle: "italic" }}>
        Category: <span style={{ color: "#111" }}>{v}</span>
      </Box>
    );
  }
  return <Box>{v}</Box>;
};

const UserSurveyContent = ({
  selectedUserEmail,
  selectedUserType,
  selections,
}) => {
  const [showAdditional, setShowAdditional] = useState(false);
  if (!selectedUserEmail) {
    return null;
  }
  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          borderRadius: "0.5rem",
          py: "8px",
          px: "16px",
        }}>
        <b>{selectedUserEmail}</b> ({selectedUserType})
      </Box>
      <Stack
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          borderRadius: "0.5rem",
          py: "8px",
          px: "16px",
        }}
        direction="column"
        spacing="0.5rem">
        {QuestionData.map((q, index) => {
          if (!showAdditional && hiddenQuestionIdx.includes(index)) {
            return null;
          }
          return (
            <Stack key={index} direction="column" spacing={1}>
              <QuestionLabel q={q} />
              {index !== 13 && (
                <Stack direction="column" sx={{}}>
                  {selections[index].length > 0 ? (
                    selections[index]
                      .map((i) => q.options[i])
                      .map((v) => {
                        return (
                          <UserSelectedValue
                            key={"u" + index}
                            questionIdx={index}
                            v={v}
                          />
                        );
                      })
                  ) : (
                    <Box key={"na" + index}>[N/A]</Box>
                  )}
                </Stack>
              )}
              {index === 13 && (
                <Box>
                  {selections[index][0] ? selections[index][0] : "[N/A]"}
                </Box>
              )}
              {/* <TempDisplay
                selected={selections}
                questionNum={index}
                options={q.options}
              /> */}
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};
const AdminPage = () => {
  const auth = useSelector((state) => state.auth);
  const selectedUserEmail = useSelector(
    (state) => state.adminSurvey.contactInfo
  );
  const selectedUserType = useSelector(
    (state) => state.adminSurvey.contactType
  );
  const selections = useSelector((state) => state.adminSurvey.selections);
  const dispatch = useDispatch();
  const [params, setParams] = React.useState({
    email: "",
    emailType: emailTypes[0],
  });
  const [loading, setLoading] = React.useState(false);
  const onSubmit = () => {
    clearError(dispatch);
    console.log(params);
    setLoading(true);
    adminService.getSurvey(
      dispatch,
      {
        ...auth.accountData,
        targetContactInfo: params.email,
        targetContactType: params.emailType,
      },
      {
        handleRequestFinish: () => setLoading(false),
      }
    );
  };
  useEffect(() => {
    console.log("new survey", selections);
  });

  return (
    <Box sx={styles.outer}>
      <Stack sx={styles.container} direction="column" alignItems="center">
        <Stack
          sx={styles.inner}
          direction="column"
          alignItems="center"
          spacing="1rem">
          <Box sx={{ ...styles.panel, ...styles.heading }}>
            Fetti Friends HQ
          </Box>
          <Stack
            sx={{ ...styles.panel, ...styles.bodyContainer }}
            direction="column"
            spacing={1}>
            <Stack direction="row" spacing="1rem">
              <LabelInputStack label="Student Email">
                <StyledTextField
                  InputProps={{ disableUnderline: true }}
                  sx={styles.input}
                  variant="standard"
                  placeholder="student@university.edu"
                  value={params.email}
                  onChange={(e) =>
                    setParams({ ...params, email: e.target.value })
                  }
                />
              </LabelInputStack>
              <SimpleSelect
                label="Email Type"
                list={emailTypes}
                value={params.emailType}
                onChange={(e) =>
                  setParams({ ...params, emailType: e.target.value })
                }
              />
            </Stack>
            {auth.error === ErrorCode.not_admin && (
              <Box sx={styles.error}>You are not an admin...</Box>
            )}
            {auth.error === ErrorCode.invalidCredentials && (
              <Box sx={styles.error}>No account found</Box>
            )}
            <Box
              onClick={onSubmit}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "0.5rem",
                ...FontStyle.body,
                py: "8px",
                textAlign: "center",
                "&:hover": {
                  cursor: "pointer",
                  backgroundColor: "#eee",
                },
                "&:active": {
                  backgroundColor: "#ddd",
                },
              }}>
              Submit
            </Box>
            {loading && <CircularProgress />}
            <UserSurveyContent
              selectedUserEmail={selectedUserEmail}
              selectedUserType={selectedUserType}
              selections={selections}
            />
          </Stack>
        </Stack>
      </Stack>
      <Box sx={styles.bg} />
      <Box sx={styles.bgImage}>
        <img src={bgImage} width="100%" height="100%" />
      </Box>
    </Box>
  );
};

export default AdminPage;
